import { Component, OnDestroy, OnInit } from '@angular/core'
import {
    Comment,
    CrmAssignee,
    Prospect,
    ProspectContact,
    ProspectInteraction,
    ProspectInteractionType,
    ProspectInteractionTypeLabels,
    ProspectInteractionTypeIcons,
    ProspectSource,
    ProspectSourceLabels,
    ProspectStatus,
    ProspectStatusColors,
    ProspectStatusLabels,
    ProspectType,
    ProspectTypeLabels,
} from '@parkupp/core'
import { Subscription } from 'rxjs'
import { ProspectingService } from 'src/app/services/prospecting.service'
import { ContactModalService } from '../contact-modal/contact-modal.service'
import { ProspectModalService } from './prospect-modal.service'

type ModalTab = 'overview' | 'contacts' | 'comments' | 'timeline'

@Component({
    selector: 'app-prospect-modal',
    templateUrl: './prospect-modal.component.html',
    styleUrls: ['./prospect-modal.component.scss'],
})
export class ProspectModalComponent implements OnInit, OnDestroy {
    prospectKey = ''
    prospect: Prospect | null = null
    contacts: ProspectContact[] = []
    comments: Comment[] = []
    interactions: ProspectInteraction[] = []
    assignees: CrmAssignee[] = []

    activeTab: ModalTab = 'overview'

    enriching = false
    busyContactKey = ''
    error = ''

    statusOptions = Object.values(ProspectStatus)
    statusLabels = ProspectStatusLabels
    statusColors = ProspectStatusColors
    sourceLabels = ProspectSourceLabels
    prospectTypeOptions = Object.values(ProspectType)
    prospectTypeLabels = ProspectTypeLabels

    interactionTypeLabels = ProspectInteractionTypeLabels
    interactionTypeIcons = ProspectInteractionTypeIcons
    interactionTypes = Object.values(ProspectInteractionType).filter(
        (t) => t !== ProspectInteractionType.STATUS_CHANGE
    )

    // Comment composer
    newComment = ''
    addingComment = false

    // Edit mode
    editing = false
    editData: Partial<Prospect> = {}

    // New contact
    showNewContact = false
    newContact: Partial<ProspectContact> = {}
    addingContact = false

    // Log interaction
    newInteraction = { type: ProspectInteractionType.NOTE as ProspectInteractionType, summary: '' }
    addingInteraction = false

    // Hamburger menu
    menuOpen = false

    // Contact interactions lookup
    contactInteractions: { [contactKey: string]: ProspectInteraction[] } = {}

    private openSub: Subscription | null = null
    private dataSubs: Subscription[] = []

    constructor(
        private prospecting: ProspectingService,
        public modal: ProspectModalService,
        private contactModal: ContactModalService
    ) {}

    ngOnInit(): void {
        this.openSub = this.modal.openKey$.subscribe((key) => {
            if (key) {
                this.load(key)
            } else {
                this.reset()
            }
        })
    }

    ngOnDestroy(): void {
        this.openSub?.unsubscribe()
        this.clearDataSubs()
    }

    private load(key: string): void {
        this.clearDataSubs()
        this.prospectKey = key
        this.activeTab = 'overview'
        this.error = ''
        this.editing = false
        this.showNewContact = false
        this.newComment = ''
        this.dataSubs.push(this.prospecting.getProspect(key).subscribe((p) => (this.prospect = p)))
        this.dataSubs.push(this.prospecting.listContactsForProspect(key).subscribe((c) => (this.contacts = c)))
        this.dataSubs.push(this.prospecting.listComments(key).subscribe((c) => (this.comments = c)))
        this.dataSubs.push(this.prospecting.listInteractionsForProspect(key).subscribe((i) => (this.interactions = i)))
        this.dataSubs.push(this.prospecting.listProspectingAssignees().subscribe((a) => (this.assignees = a)))
    }

    private reset(): void {
        this.clearDataSubs()
        this.prospectKey = ''
        this.prospect = null
        this.contacts = []
        this.comments = []
    }

    private clearDataSubs(): void {
        this.dataSubs.forEach((s) => s.unsubscribe())
        this.dataSubs = []
    }

    close(): void {
        this.modal.close()
    }

    setTab(tab: ModalTab): void {
        this.activeTab = tab
    }

    statusLabel(): string {
        if (!this.prospect) return ''
        return this.statusLabels[this.prospect.status] || this.prospect.status
    }

    statusColor(status: ProspectStatus): string {
        return this.statusColors[status] || '#6b7280'
    }

    async changeStatus(status: ProspectStatus): Promise<void> {
        if (!this.prospect?.$key || this.prospect.status === status) return
        const previousStatus = this.prospect.status
        try {
            await this.prospecting.updateProspectStatus(this.prospect.$key, status, previousStatus)
        } catch (e: any) {
            this.error = e?.message || 'Could not update status'
        }
    }

    async changeProspectType(type: string): Promise<void> {
        if (!this.prospect?.$key) return
        try {
            await this.prospecting.updateProspect(this.prospect.$key, { prospectType: (type || null) as any })
        } catch (e: any) {
            this.error = e?.message || 'Could not update prospect type'
        }
    }

    async enrich(includeContacts: boolean): Promise<void> {
        if (!this.prospect?.$key) return
        this.enriching = true
        this.error = ''
        try {
            await this.prospecting.enrichProspect(this.prospect.$key, includeContacts)
        } catch (e: any) {
            this.error = e?.message || 'Enrichment failed'
        } finally {
            this.enriching = false
        }
    }

    async verifyEmail(c: ProspectContact): Promise<void> {
        if (!c.$key) return
        this.busyContactKey = c.$key
        try {
            await this.prospecting.verifyContactEmail(c.$key)
        } catch (e: any) {
            this.error = e?.message || 'Verify failed'
        } finally {
            this.busyContactKey = ''
        }
    }

    async findEmail(c: ProspectContact): Promise<void> {
        if (!c.$key) return
        this.busyContactKey = c.$key
        try {
            const res = await this.prospecting.findContactEmail(c.$key)
            if (!res?.email) this.error = 'No email found for this contact'
        } catch (e: any) {
            this.error = e?.message || 'Find email failed'
        } finally {
            this.busyContactKey = ''
        }
    }

    async scrapeLinkedIn(c: ProspectContact): Promise<void> {
        if (!c.$key) return
        const url = c.linkedInUrl || prompt('LinkedIn profile URL for ' + (c.fullName || c.email) + ':') || ''
        if (!url) return
        this.busyContactKey = c.$key
        try {
            await this.prospecting.scrapeContactLinkedIn(c.$key, url)
        } catch (e: any) {
            this.error = e?.message || 'LinkedIn scrape failed'
        } finally {
            this.busyContactKey = ''
        }
    }

    openContact(c: ProspectContact): void {
        if (!c.$key) return
        this.contactModal.open(c.$key)
    }

    contactName(c: ProspectContact): string {
        return c.fullName || [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email
    }

    contactAvatar(c: ProspectContact): string {
        return c.linkedInProfilePicStored || c.linkedInProfilePic || ''
    }

    contactInitials(c: ProspectContact): string {
        const name = this.contactName(c)
        const parts = name.split(/[\s@.]+/).filter(Boolean)
        return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase()
    }

    async addComment(): Promise<void> {
        const text = this.newComment.trim()
        if (!text || !this.prospectKey || this.addingComment) return
        this.addingComment = true
        try {
            await this.prospecting.addComment(this.prospectKey, text)
            this.newComment = ''
        } catch (e: any) {
            this.error = e?.message || 'Could not add comment'
        } finally {
            this.addingComment = false
        }
    }

    // ---------- Edit ----------

    startEdit(): void {
        if (!this.prospect) return
        this.editData = {
            name: this.prospect.name,
            fullAddress: this.prospect.fullAddress,
            website: this.prospect.website,
            phone: this.prospect.phone,
            notes: this.prospect.notes,
            linkedInUrl: this.prospect.linkedInUrl,
        }
        this.editing = true
    }

    cancelEdit(): void {
        this.editing = false
    }

    async saveEdit(): Promise<void> {
        if (!this.prospect?.$key) return
        try {
            await this.prospecting.updateProspect(this.prospect.$key, this.editData)
            this.editing = false
        } catch (e: any) {
            this.error = e?.message || 'Could not save changes'
        }
    }

    // ---------- Assignment ----------

    async assignTo(uid: string | null): Promise<void> {
        if (!this.prospect?.$key) return
        const assignee = uid ? this.assignees.find((a) => a.$key === uid) : null
        try {
            await this.prospecting.assignProspect(this.prospect.$key, uid, assignee?.displayName || null)
        } catch (e: any) {
            this.error = e?.message || 'Could not assign prospect'
        }
    }

    // ---------- New Contact ----------

    toggleNewContact(): void {
        this.showNewContact = !this.showNewContact
        if (this.showNewContact) {
            this.newContact = { prospectKey: this.prospectKey, denormProspectName: this.prospect?.name }
        }
    }

    async saveNewContact(): Promise<void> {
        if (!this.newContact.email && !this.newContact.fullName) return
        this.addingContact = true
        try {
            await this.prospecting.createContact(this.newContact)
            this.showNewContact = false
            this.newContact = {}
        } catch (e: any) {
            this.error = e?.message || 'Could not add contact'
        } finally {
            this.addingContact = false
        }
    }

    // ---------- Interactions ----------

    async logInteraction(): Promise<void> {
        if (!this.newInteraction.summary.trim() || this.addingInteraction) return
        this.addingInteraction = true
        try {
            await this.prospecting.addInteraction({
                prospectKey: this.prospectKey,
                denormProspectName: this.prospect?.name,
                type: this.newInteraction.type,
                summary: this.newInteraction.summary,
            })
            this.newInteraction = { type: ProspectInteractionType.NOTE, summary: '' }
        } catch (e: any) {
            this.error = e?.message || 'Could not log interaction'
        } finally {
            this.addingInteraction = false
        }
    }

    interactionDate(i: ProspectInteraction): Date | null {
        const ts: any = i.createdAt
        if (!ts) return null
        if (ts.toDate) return ts.toDate()
        return new Date(ts)
    }

    // ---------- Hamburger menu ----------

    toggleMenu(): void {
        this.menuOpen = !this.menuOpen
    }

    closeMenu(): void {
        this.menuOpen = false
    }

    // ---------- Primary contact ----------

    async togglePrimary(c: ProspectContact): Promise<void> {
        if (!c.$key) return
        try {
            await this.prospecting.updateContact(c.$key, { isPrimary: !c.isPrimary })
        } catch (e: any) {
            this.error = e?.message || 'Could not update contact'
        }
    }

    // ---------- Per-contact activity ----------

    contactInteractionCount(contactKey?: string): number {
        if (!contactKey) return 0
        return this.interactions.filter((i) => i.contactKey === contactKey).length
    }

    contactLastInteraction(contactKey?: string): ProspectInteraction | null {
        if (!contactKey) return null
        return this.interactions.find((i) => i.contactKey === contactKey) || null
    }
}
